// AUDITORIA-CLINICA-REDISENO (FASE 2) -- lógica PURA de una corrección hecha
// desde la tarjeta de sección: estado inicial, detección de cambio real,
// validación y construcción del payload.
//
// El contrato con el backend es EXACTAMENTE el que ya existe
// (`POST /audit/clinical/appointments/:idcita/corrections`, ver
// FAuditClinicalCorrection.ts): cada `change` lleva su propio
// `reason_code` + `justification` y, cuando aplica, `_expected` como snapshot
// de concurrencia. La diferencia con el modal actual es sólo la SUPERFICIE
// (una tarjeta en sitio, una sección por vez), no el contrato.
//
// Vive separado del componente para poder probar el payload sin renderizar.

export const REASON_CODE_OPTIONS = [
    { value: "ERROR_DIGITACION", label: "Error de digitación" },
    { value: "INFORMACION_INCOMPLETA", label: "Información incompleta" },
    { value: "ORDEN_INCORRECTA", label: "Orden incorrecta" },
    { value: "DIAGNOSTICO_INCORRECTO", label: "Diagnóstico incorrecto" },
    { value: "CORRECCION_CLINICA", label: "Corrección clínica" },
    { value: "OTRO", label: "Otro" },
];
const REASON_CODES = REASON_CODE_OPTIONS.map((o) => o.value);

export const JUSTIFICACION_MIN = 10;
export const JUSTIFICACION_MAX = 1000;

// Secciones de texto libre: mismo formulario (valor original vs corregido),
// distinta tabla y distinto nombre de campo/PK.
// COMPLETAR-SECCION-VACIA: las secciones que se pueden CREAR cuando el médico
// no las registró. Son exactamente las que el backend sabe crear
// (`applyEnfermedadActual`, `applyPlanTratamiento`, `applyNotaEvolucion`).
//
// `motivo_consulta` NO está porque vive en la propia cita: siempre existe.
//
// AUD-EDIT-02: `signos_vitales` se agrega -- la regla de negocio pasó de
// "no se inventa una medición" a "todo punto ausente debe poder completarse".
// El backend (`applySignosVitales`) ya soporta CREATE cuando no llega
// `idsignosvitales`, con antes=null por campo y sin tocar `citas.id_estado_cita`.
//
// AUD-EDIT-QA-03: `diagnosticos` también se agrega. Se había dejado fuera con
// el argumento de que su patrón ADD/REMOVE ya resuelve "agregar lo que falta",
// pero eso sólo era cierto cuando la cita YA tenía algún diagnóstico: con la
// colección vacía la sección cae en "sin registro", y sin botón no había forma
// de abrir el editor para agregar el primero. El backend (`applyDiagnosticos`)
// siempre lo soportó -- `current` vacío y `_expected: []` son un caso válido.
//
// `recetas` sigue fuera -- crear una prescripción desde cero implica
// atribuírsela a un médico que nunca la emitió (ver AUD-EDIT-02,
// BLOQUEO_ESTRUCTURAL de recetas); completar sólo tiene sentido sobre lo que
// ya existe.
export const SECCIONES_COMPLETABLES = ["enfermedad_actual", "plan_tratamiento", "nota_evolucion", "signos_vitales", "diagnosticos"];

export const SECCIONES_TEXTO = {
    motivo_consulta: { campo: "motivo", idCampo: null, coleccion: null },
    enfermedad_actual: { campo: "enfermedad_actual", idCampo: "idenfermedad_actual_cita", coleccion: "enfermedad_actual" },
    plan_tratamiento: { campo: "plan_tratamiento", idCampo: "idplan_tratamiento", coleccion: "plan_tratamiento" },
    nota_evolucion: { campo: "nota_evolucion", idCampo: "idnota_evolucion", coleccion: "nota_evolucion" },
};

// SIGNOS-CAMPOS-FIX: se corrigen los 8 campos MEDIDOS que acepta el backend
// (`SIGNOS_FIELDS` en FAuditClinicalCorrection.ts). Antes sólo había 4 --
// heredado del modal-- y faltaban talla, peso, frecuencia respiratoria y
// perímetro cefálico, que son datos que el médico sí registra y que por tanto
// también pueden estar mal digitados.
export const SIGNOS_EDITABLES = [
    { id: "presion", label: "Presión" },
    { id: "pulso", label: "Pulso" },
    { id: "temperatura", label: "Temperatura" },
    { id: "frecuencia_respiratoria", label: "Frec. respiratoria" },
    { id: "saturacion_oxigeno", label: "Saturación O2" },
    { id: "peso", label: "Peso (kg)" },
    { id: "talla", label: "Talla (m)" },
    { id: "perimetro_cefalico", label: "Perímetro cefálico" },
];

// El IMC es DERIVADO, no un dato que se digite: el formulario clínico real lo
// calcula como `peso / talla²` (src/Page/SigVital.js). Por eso no es editable
// aquí -- se recalcula solo cuando cambia el peso o la talla y viaja dentro
// del mismo cambio, para no dejar nunca un IMC incoherente con el peso
// corregido. El backend lo guarda como un campo más y no lo recalcula.
export const SIGNO_IMC = { id: "imc", label: "IMC" };

/** Misma fórmula que el formulario clínico, redondeada a 2 decimales. */
export function calcularImc(peso, talla) {
    const p = Number(peso);
    const t = Number(talla);
    if (!Number.isFinite(p) || !Number.isFinite(t) || p <= 0 || t <= 0) return null;
    const imc = p / Math.pow(t, 2);
    if (!Number.isFinite(imc)) return null;
    return Math.round((imc + Number.EPSILON) * 100) / 100;
}

export const RECETA_CAMPOS = [
    { id: "medicamentos", label: "Medicamento", obligatorio: true },
    { id: "indicaciones", label: "Indicaciones", obligatorio: true },
    { id: "posologia", label: "Posología" },
    { id: "cantidad", label: "Cantidad" },
    { id: "dosis", label: "Dosis" },
];

export const esRecetaVigente = (r) => (r?.estado || "Activa") === "Activa";

const texto = (v) => (v === null || v === undefined ? "" : String(v));
const normalizarNumero = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const lista = (v) => (Array.isArray(v) ? v : []);

/**
 * ¿Es válida la razón de un cambio puntual?
 *
 * JUSTIFICACION-OPCIONAL: el MOTIVO sigue siendo obligatorio -- sin él no se
 * sabe por qué se corrigió --, pero la justificación pasa a ser opcional.
 * Obligar a redactar diez caracteres más en cada cambio sólo producía texto de
 * relleno ("corrección", "se corrige") que no informaba a nadie.
 *
 * Opcional NO significa "vale cualquier cosa": si se escribe, se sigue
 * exigiendo el mínimo y el máximo de siempre.
 */
export function razonValida(razon) {
    if (!razon || !REASON_CODES.includes(razon.reason_code)) return false;
    const j = texto(razon.justification).trim();
    if (j.length === 0) return true;
    return j.length >= JUSTIFICACION_MIN && j.length <= JUSTIFICACION_MAX;
}

/**
 * Estado inicial de la edición de una sección: el valor corregido arranca
 * IGUAL al original (el auditor edita sobre lo que ve) y ningún cambio se
 * considera real hasta que difiera.
 */
export function iniciarEdicion(idSeccion, secciones) {
    const s = secciones || {};
    const base = { seccion: idSeccion, registroId: null, valores: {}, razones: {}, accion: null, diagnosticos: null };

    if (idSeccion === "motivo_consulta") {
        return { ...base, valores: { motivo: texto(s.motivo) } };
    }
    if (SECCIONES_TEXTO[idSeccion]) {
        const { campo, idCampo, coleccion } = SECCIONES_TEXTO[idSeccion];
        const registros = lista(s[coleccion]);
        const primero = registros[0] || null;
        return {
            ...base,
            registroId: primero ? primero[idCampo] : null,
            valores: { [campo]: texto(primero?.[campo]) },
        };
    }
    if (idSeccion === "signos_vitales") {
        const primero = lista(s.signos_vitales)[0] || null;
        const valores = {};
        for (const campo of SIGNOS_EDITABLES) valores[campo.id] = texto(primero?.[campo.id]);
        return { ...base, registroId: primero ? primero.idsignosvitales : null, valores };
    }
    if (idSeccion === "diagnosticos") {
        return {
            ...base,
            diagnosticos: lista(s.diagnosticos).map((d) => ({
                iddiagnostico: d.iddiagnostico,
                codigo: d.diagnostico?.codigo ?? null,
                nombre: d.diagnostico?.nombre ?? null,
            })),
        };
    }
    if (idSeccion === "recetas") {
        const vigente = lista(s.recetas).filter(esRecetaVigente)[0] || null;
        const valores = {};
        for (const campo of RECETA_CAMPOS) valores[campo.id] = texto(vigente?.[campo.id]);
        return { ...base, registroId: vigente ? vigente.idreceta : null, accion: "REPLACE", valores };
    }
    return base;
}

/** Registro (fila) sobre el que se está trabajando, releído de `secciones`. */
export function registroSeleccionado(edicion, secciones) {
    const s = secciones || {};
    if (!edicion) return null;
    if (edicion.seccion === "signos_vitales") {
        return lista(s.signos_vitales).find((r) => r.idsignosvitales === edicion.registroId) || null;
    }
    if (edicion.seccion === "recetas") {
        return lista(s.recetas).find((r) => r.idreceta === edicion.registroId) || null;
    }
    const cfg = SECCIONES_TEXTO[edicion.seccion];
    if (cfg && cfg.coleccion) {
        return lista(s[cfg.coleccion]).find((r) => r[cfg.idCampo] === edicion.registroId) || null;
    }
    return null;
}

/** Valor ORIGINAL de un campo de texto, tal como está hoy en la atención. */
export function valorOriginal(edicion, secciones) {
    if (edicion?.seccion === "motivo_consulta") return texto((secciones || {}).motivo);
    const cfg = SECCIONES_TEXTO[edicion?.seccion];
    if (!cfg) return "";
    return texto(registroSeleccionado(edicion, secciones)?.[cfg.campo]);
}

/** Diagnósticos agregados/retirados respecto de la colección actual. */
export function diffDiagnosticos(edicion, secciones) {
    const actuales = lista((secciones || {}).diagnosticos).map((d) => ({
        iddiagnostico: d.iddiagnostico,
        codigo: d.diagnostico?.codigo ?? null,
        nombre: d.diagnostico?.nombre ?? null,
    }));
    const seleccion = lista(edicion?.diagnosticos);
    const idsActuales = actuales.map((d) => d.iddiagnostico);
    const idsSeleccion = seleccion.map((d) => d.iddiagnostico);
    return {
        actuales,
        idsActuales,
        agregados: seleccion.filter((d) => !idsActuales.includes(d.iddiagnostico)),
        retirados: actuales.filter((d) => !idsSeleccion.includes(d.iddiagnostico)),
    };
}

/**
 * Campos de signos vitales realmente editados respecto del registro actual.
 *
 * AUD-EDIT-02: sin registro (la sección está ausente), "editado" es
 * cualquier campo con contenido -- mismo criterio que las secciones de texto
 * completables (`hayCambioReal`). Antes esto devolvía `[]` siempre que no
 * había registro y el botón de guardar nunca se activaba.
 */
export function signosEditados(edicion, secciones) {
    const registro = registroSeleccionado(edicion, secciones);
    if (!registro) {
        return SIGNOS_EDITABLES.filter((c) => texto(edicion.valores?.[c.id]).trim() !== "").map((c) => c.id);
    }
    return SIGNOS_EDITABLES.filter((c) => texto(edicion.valores?.[c.id]) !== texto(registro[c.id])).map((c) => c.id);
}

/** Campos de la receta modificados respecto de la versión vigente. */
export function recetaEditada(edicion, secciones) {
    const registro = registroSeleccionado(edicion, secciones);
    if (!registro) return [];
    return RECETA_CAMPOS.filter((c) => texto(edicion.valores?.[c.id]) !== texto(registro[c.id])).map((c) => c.id);
}

/**
 * Claves de razón que hay que completar para poder guardar. Una sola para las
 * secciones de texto y para la receta; una POR CAMPO en signos vitales y una
 * POR OPERACIÓN en diagnósticos -- porque dos cambios de la misma corrección
 * pueden tener razones clínicas distintas (regla ya vigente en el backend).
 */
export function razonesRequeridas(edicion, secciones) {
    if (!edicion) return [];
    if (SECCIONES_TEXTO[edicion.seccion]) return [{ clave: edicion.seccion, etiqueta: "Razón de este cambio" }];
    if (edicion.seccion === "signos_vitales") {
        // AUD-EDIT-02: al COMPLETAR (sin registro) es UNA sola operación de
        // creación -- una sola razón, igual que enfermedad_actual/
        // plan_tratamiento/nota_evolucion. Pedir una razón por campo no tiene
        // sentido para algo que nunca existió.
        if (!edicion.registroId) return [{ clave: "signos_vitales", etiqueta: "Razón de esta creación" }];
        return signosEditados(edicion, secciones).map((campo) => ({
            clave: `signo:${campo}`,
            etiqueta: `Razón del cambio en ${(SIGNOS_EDITABLES.find((c) => c.id === campo) || {}).label || campo}`,
        }));
    }
    if (edicion.seccion === "diagnosticos") {
        const { agregados, retirados } = diffDiagnosticos(edicion, secciones);
        return [
            ...agregados.map((d) => ({ clave: `add:${d.iddiagnostico}`, etiqueta: `Razón para agregar ${d.codigo || d.iddiagnostico}` })),
            ...retirados.map((d) => ({ clave: `remove:${d.iddiagnostico}`, etiqueta: `Razón para retirar ${d.codigo || d.iddiagnostico}` })),
        ];
    }
    if (edicion.seccion === "recetas") return [{ clave: "recetas", etiqueta: "Razón de esta corrección" }];
    return [];
}

/** ¿El auditor cambió realmente algo? Guardar sin cambio es un no-op. */
export function hayCambioReal(edicion, secciones) {
    if (!edicion) return false;
    if (SECCIONES_TEXTO[edicion.seccion]) {
        const cfg = SECCIONES_TEXTO[edicion.seccion];
        // COMPLETAR-SECCION-VACIA: sin registro previo, escribir algo YA es un
        // cambio real -- es justo el caso de "el médico no registró esta
        // sección". Antes esto devolvía false y el botón de guardar no se
        // activaba nunca.
        if (!edicion.registroId && edicion.seccion !== "motivo_consulta") {
            if (!SECCIONES_COMPLETABLES.includes(edicion.seccion)) return false;
            return texto(edicion.valores?.[cfg.campo]).trim().length > 0;
        }
        return texto(edicion.valores?.[cfg.campo]) !== valorOriginal(edicion, secciones);
    }
    if (edicion.seccion === "signos_vitales") return signosEditados(edicion, secciones).length > 0;
    if (edicion.seccion === "diagnosticos") {
        const { agregados, retirados } = diffDiagnosticos(edicion, secciones);
        return agregados.length > 0 || retirados.length > 0;
    }
    if (edicion.seccion === "recetas") {
        if (!edicion.registroId) return false;
        // Anular una receta es un cambio en sí mismo, sin editar campos.
        if (edicion.accion === "VOID") return true;
        return recetaEditada(edicion, secciones).length > 0;
    }
    return false;
}

/** Motivo por el que aún no se puede guardar, o null si ya se puede. */
export function motivoNoGuardable(edicion, secciones) {
    if (!edicion) return "No hay ninguna corrección en curso.";
    // AUD-EDIT-02 (bug encontrado, no sólo de signos_vitales): esta guarda
    // bloqueaba TODA sección de texto sin registro con "selecciona el
    // registro" -- incluidas enfermedad_actual/plan_tratamiento/
    // nota_evolucion, que sí son completables desde COMPLETAR-SECCION-VACIA.
    // El botón "Guardar" nunca se habilitaba para completar nada; sólo se
    // había probado con fixtures donde el registro siempre existía. Ahora se
    // excluye a las secciones completables -- sin registro ahí no significa
    // "elige uno", significa "complétalo".
    if (
        SECCIONES_TEXTO[edicion.seccion] &&
        edicion.seccion !== "motivo_consulta" &&
        !edicion.registroId &&
        !SECCIONES_COMPLETABLES.includes(edicion.seccion)
    ) {
        return "Selecciona el registro que quieres corregir.";
    }
    // `signos_vitales` está en SECCIONES_COMPLETABLES pero no en
    // SECCIONES_TEXTO (tiene su propio bloque abajo, ya sin este bloqueo).
    // `recetas` se queda bloqueada: crear una receta desde cero es un
    // bloqueo estructural distinto (ver AUD-EDIT-02 §G).
    if (edicion.seccion === "recetas" && !edicion.registroId) {
        return "Selecciona el registro que quieres corregir.";
    }
    if (edicion.seccion === "recetas" && edicion.accion === "REPLACE") {
        for (const campo of RECETA_CAMPOS.filter((c) => c.obligatorio)) {
            if (texto(edicion.valores?.[campo.id]).trim() === "") return `«${campo.label}» es obligatorio en la receta que sustituye.`;
        }
    }
    if (!hayCambioReal(edicion, secciones)) return "Modifica el valor para poder guardar la corrección.";
    const faltantes = razonesRequeridas(edicion, secciones).filter((r) => !razonValida(edicion.razones?.[r.clave]));
    if (faltantes.length === 1) return "Indica el motivo del cambio y una justificación de al menos 10 caracteres.";
    if (faltantes.length > 1) return `Faltan el motivo y la justificación de ${faltantes.length} cambios.`;
    return null;
}

export const edicionValida = (edicion, secciones) => motivoNoGuardable(edicion, secciones) === null;

const razonPayload = (edicion, clave) => ({
    reason_code: edicion.razones?.[clave]?.reason_code,
    justification: texto(edicion.razones?.[clave]?.justification).trim(),
});

/**
 * Payload `changes` de UNA sección. Cada corrección se guarda por sección,
 * nunca en lote de secciones distintas (regla del rediseño).
 */
export function construirCambios(edicion, secciones) {
    if (!edicion) return [];
    const s = secciones || {};

    if (edicion.seccion === "motivo_consulta") {
        return [
            {
                section: "motivo_consulta",
                data: {
                    motivo: edicion.valores.motivo,
                    ...razonPayload(edicion, "motivo_consulta"),
                    _expected: { motivo: s.motivo ?? null },
                },
            },
        ];
    }

    const cfg = SECCIONES_TEXTO[edicion.seccion];
    if (cfg) {
        const registro = registroSeleccionado(edicion, secciones);
        return [
            {
                section: edicion.seccion,
                data: {
                    [cfg.idCampo]: edicion.registroId,
                    [cfg.campo]: edicion.valores[cfg.campo],
                    ...razonPayload(edicion, edicion.seccion),
                    _expected: { [cfg.campo]: registro?.[cfg.campo] ?? null },
                },
            },
        ];
    }

    if (edicion.seccion === "signos_vitales") {
        const registro = registroSeleccionado(edicion, secciones);
        const editados = signosEditados(edicion, secciones);

        // AUD-EDIT-02: sin registro, es UNA sola creación con TODOS los
        // campos con contenido en el mismo `change` -- nunca un change por
        // campo (eso crearía una fila por cada campo, chocando con el guard
        // anti-duplicado del backend). Una sola razón para toda la creación.
        if (!edicion.registroId) {
            const data = { ...razonPayload(edicion, "signos_vitales") };
            for (const campo of editados) data[campo] = edicion.valores[campo] === "" ? null : edicion.valores[campo];
            const imc = calcularImc(edicion.valores.peso, edicion.valores.talla);
            if (imc !== null) data.imc = imc;
            return [{ section: "signos_vitales", data }];
        }

        // Un change POR CAMPO editado, cada uno con su razón y su `_expected`
        // acotado al campo que se toca (mismo contrato que MODAL-45).
        const cambios = editados.map((campo) => ({
            section: "signos_vitales",
            data: {
                idsignosvitales: edicion.registroId,
                [campo]: edicion.valores[campo] === "" ? null : edicion.valores[campo],
                ...razonPayload(edicion, `signo:${campo}`),
                _expected: { [campo]: registro?.[campo] ?? null },
            },
        }));

        // El IMC recalculado viaja PEGADO al último cambio de peso/talla (no
        // como un cambio suelto): es una consecuencia de ese cambio, comparte
        // su motivo y evita pedir una justificación por un dato derivado.
        const disparadores = editados.filter((c) => c === "peso" || c === "talla");
        if (disparadores.length > 0) {
            const imc = calcularImc(edicion.valores.peso, edicion.valores.talla);
            if (imc !== null && imc !== normalizarNumero(registro?.imc)) {
                const ultimo = cambios[editados.indexOf(disparadores[disparadores.length - 1])];
                ultimo.data.imc = imc;
            }
        }
        return cambios;
    }

    if (edicion.seccion === "diagnosticos") {
        const { agregados, retirados, idsActuales } = diffDiagnosticos(edicion, secciones);
        return [
            {
                section: "diagnosticos",
                data: {
                    added: agregados.map((d) => ({ iddiagnostico: d.iddiagnostico, ...razonPayload(edicion, `add:${d.iddiagnostico}`) })),
                    removed: retirados.map((d) => ({ iddiagnostico: d.iddiagnostico, ...razonPayload(edicion, `remove:${d.iddiagnostico}`) })),
                    _expected: idsActuales,
                },
            },
        ];
    }

    if (edicion.seccion === "recetas") {
        const registro = registroSeleccionado(edicion, secciones);
        const expected = RECETA_CAMPOS.reduce((acc, c) => ({ ...acc, [c.id]: registro?.[c.id] ?? null }), {
            idreceta: registro?.idreceta ?? null,
            estado: registro?.estado || "Activa",
        });
        const data = {
            action: edicion.accion,
            original_id: edicion.registroId,
            ...razonPayload(edicion, "recetas"),
            _expected: expected,
        };
        if (edicion.accion === "REPLACE") {
            data.replacement = RECETA_CAMPOS.reduce((acc, c) => ({ ...acc, [c.id]: edicion.valores[c.id] }), {});
        }
        return [{ section: "recetas", data }];
    }

    return [];
}
